import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdatePlayer from '@/modules/manage-player/api/hooks/useMutationUpdatePlayer'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const formSchema = z.object({
	password: z
		.string({ required_error: 'โปรดกรอกรหัสผ่าน' })
		.min(4, 'โปรดกรอกรหัสผ่าน 4 ตัว')
		.max(4, 'โปรดกรอกรหัสผ่าน 4 ตัว'),
})

type FormValues = z.infer<typeof formSchema>

interface PlayerResetPasswordModalProps {
	playerId: string
	playerUsername: string
}

export const PlayerResetPasswordModal = ({
	playerId,
	playerUsername,
}: PlayerResetPasswordModalProps) => {
	const [open, setOpen] = useState(false)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			password: '',
		},
	})

	const { mutate: updatePlayer } = useMutationUpdatePlayer()

	const onSubmit = (values: FormValues) => {
		try {
			updatePlayer(
				{ id: playerId, payload: values },
				{
					onSuccess: () => {
						toast.success('เปลี่ยนรหัสผ่านเรียบร้อยแล้ว')
						form.reset()
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="outline" onClick={() => setOpen(true)}>
				เปลี่ยนรหัสผ่าน
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>เปลี่ยนรหัสผ่าน ({playerUsername})</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> รหัสผ่านใหม่
										</FormLabel>
										<FormControl>
											<Input
												type="password"
												inputMode="numeric"
												pattern="[0-9]*"
												placeholder="ตัวเลข 4 ตัว"
												maxLength={4}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4">
								<Button
									variant="outline"
									onClick={() => setOpen(false)}
									type="button"
								>
									ยกเลิก
								</Button>
								<Button type="submit">บันทึก</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
